<template>
    <vx-expandable-card>
        <template v-slot:header> Address </template>
        <template v-slot:content>
            <div class="flex flex-col gap-4">
                <div>
                    <vx-label size="md">Address Line 1</vx-label>

                    <vx-input
                        data-test="address-input"
                        v-model="form.address"
                        size="lg"
                        placeholder="Address"
                        :error="displayFirstError(validate.address.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md" class="w-full">
                        <div class="flex items-center justify-between flex-grow">
                            <span>Address Line 2</span>
                            <vx-badge color="slate" size="xs">
                                <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                            </vx-badge>
                        </div>
                    </vx-label>

                    <vx-input
                        data-test="address-line-2-input"
                        v-model="form.address_line_2"
                        size="lg"
                        placeholder="Address Line 2"
                        :error="displayFirstError(validate.address_line_2.$errors)"
                    ></vx-input>
                </div>

                <div data-test="country-select">
                    <vx-label size="md">Country</vx-label>
                    <vx-selectable
                        v-model="form.country"
                        :options="countriesList"
                        :reduce="(option) => option.value"
                        size="lg"
                        :clearable="true"
                        :error="displayFirstError(validate.country.$errors)"
                    ></vx-selectable>
                </div>

                <div>
                    <vx-label size="md">City</vx-label>

                    <vx-input
                        data-test="city-input"
                        v-model="form.city"
                        size="lg"
                        placeholder="City"
                        :error="displayFirstError(validate.city.$errors)"
                    ></vx-input>
                </div>

                <div data-test="state-select">
                    <vx-label size="md">State</vx-label>

                    <vx-selectable
                        v-if="showStateDropdown"
                        v-model="form.state"
                        :options="statesList[form.country || 'US']"
                        :reduce="(option) => option.value"
                        size="lg"
                        :clearable="true"
                        :error="displayFirstError(validate.state.$errors)"
                        @update:modelValue="onStatePicked"
                    ></vx-selectable>
                    <vx-input
                        v-else
                        data-test="state-input"
                        v-model="form.state"
                        size="lg"
                        placeholder="State"
                        :error="displayFirstError(validate.state.$errors)"
                    ></vx-input>
                </div>

                <div>
                    <vx-label size="md">Zip</vx-label>

                    <vx-input
                        data-test="zip-input"
                        v-model="form.zip"
                        size="lg"
                        placeholder="Zip"
                        :error="displayFirstError(validate.zip.$errors)"
                    ></vx-input>
                </div>

                <div data-test="time-zone-select">
                    <vx-label size="md">Time Zone</vx-label>

                    <vx-selectable
                        v-model="form.time_zone"
                        :options="timeZonesList"
                        size="lg"
                        :reduce="(option) => option.value"
                        placeholder="Select a time zone"
                        :clearable="true"
                        :error="displayFirstError(validate.time_zone.$errors)"
                    ></vx-selectable>

                    <div class="mt-2">
                        <vx-button data-test="derive-timezone-button" @click="deriveNow" size="sm" color="muted">
                            Derive time zone from address
                        </vx-button>
                    </div>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script setup>
import { VxBadge, VxButton, VxExpandableCard, VxInput, VxLabel, VxSelectable } from '@voxie/frontend-components';
import { computed } from 'vue';
import { timeZoneOptions } from '~/utils/timeZones';
import { displayFirstError } from '~/utils/validation';
import { alphaNum, helpers, maxLength, minLength, numeric } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { countries as countriesList, states as statesList } from '~/constants/countries';
import { timezoneFromZipCode, timezoneFromCanadianProvince } from '~/utils/zipToTimezone';
import { useToasted } from '~/composables/useToasted';
import { useAddressStateSelector } from '~/composables/useAddressStateSelector';

const toasted = useToasted();

const form = defineModel({
    required: true,
    type: Object,
});

const timeZonesList = computed(() =>
    timeZoneOptions(form.value.time_zone).map((timeZone) => ({
        value: timeZone,
        label: timeZone.split('_').join(' '),
    }))
);

const getDerivedTimezone = () => {
    if (form.value.country === 'US' && form.value.zip && form.value.zip.length === 5) {
        return timezoneFromZipCode(form.value.zip);
    }

    if (form.value.country === 'CA' && form.value.state) {
        return timezoneFromCanadianProvince(form.value.state);
    }

    return null;
};

const deriveNow = () => {
    const tz = getDerivedTimezone();
    if (tz === null) {
        toasted.error('Could not derive time zone. Set country and a valid ZIP (US) or state (Canada).');
        return;
    }
    form.value.time_zone = tz;
};

const { showStateDropdown, onStatePicked } = useAddressStateSelector(form);

const validationRules = computed(() => {
    const zipRules = {
        alphaNum: helpers.withMessage('The ZIP code must be alpha-numeric', alphaNum),
        maxLength: maxLength(25),
    };

    if (['US', 'CA'].includes(form.value.country)) {
        const zipCodeLength = form.value.country === 'CA' ? 6 : 5;
        zipRules.minLength = minLength(zipCodeLength);
        zipRules.maxLength = maxLength(zipCodeLength);

        if (form.value.country === 'US') {
            delete zipRules.alphaNum;
            zipRules.numeric = helpers.withMessage('The ZIP code must only contain numbers', numeric);
        }
    }

    return {
        time_zone: {},
        address: { maxLength: maxLength(100) },
        address_line_2: { maxLength: maxLength(100) },
        city: { maxLength: maxLength(50) },
        state: { maxLength: showStateDropdown.value ? maxLength(2) : maxLength(50) },
        zip: zipRules,
        country: {},
    };
});

const validate = useVuelidate(validationRules, form);
</script>
